import { useEffect, useState } from 'react';
import { Button, Input, List, Modal, Space, Tag, Typography, message } from 'antd';
import { useApi } from '../../hooks/use_api';
import { useAuth } from '../../context/AuthContext';
import { EntityImage } from '../Entities/EntityImage';

const { Text } = Typography;

// Install an existing app instance in a further entity (backend
// routers/apps.py install_app_into: POST /api/apps/{id}/hosts). One channel,
// any number of hosts: the people of the new host join the same conversation.
// The list offers what you could be an admin of — your own person entity and
// the contexts and relationships you are connected to; the backend decides,
// so a pick you do not administer comes back as a 403.
function AddHostModal({ open, onClose, instance, onAdded }) {
    const api = useApi();
    const { userPersonEntity } = useAuth();
    const [candidates, setCandidates] = useState(null);
    const [filter, setFilter] = useState('');
    const [busy, setBusy] = useState(null);

    useEffect(() => {
        if (!open || !userPersonEntity?.id) return;
        setCandidates(null);
        api.get(`/api/entities/${userPersonEntity.id}/connected`)
            .then(rows => {
                const groups = rows
                    .filter(c => c.connected_status === true && ['context', 'relationship'].includes(c.entity.entity_type))
                    .map(c => c.entity);
                setCandidates([{ ...userPersonEntity, entity_type: 'person' }, ...groups]);
            })
            .catch(() => setCandidates([]));
    }, [api, open, userPersonEntity]);

    const taken = new Set(instance?.host_ids || []);
    const shown = (candidates || [])
        .filter(e => !taken.has(e.id))
        .filter(e => !filter.trim() || (e.name || '').toLowerCase().includes(filter.trim().toLowerCase()));

    const add = entity => {
        setBusy(entity.id);
        api.post(`/api/apps/${instance.id}/hosts`, { host_id: entity.id })
            .then(() => {
                message.success(`Installed in ${entity.name}`);
                onAdded?.();
                onClose();
            })
            .catch(err => message.error(err.status === 403
                ? 'You must be an admin of both this app and that entity'
                : (err.message || 'Could not install it there')))
            .finally(() => setBusy(null));
    };

    return (
        <Modal title={`Install ${instance?.name || 'this app'} in another entity`} open={open} onCancel={onClose} footer={null}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 13 }}>
                The people of that entity join the same {instance?.kind === 'channel' ? 'conversation' : 'app'}; an admin there can uninstall it again.
            </Text>
            <Input allowClear placeholder="Filter by name" value={filter} onChange={e => setFilter(e.target.value)} style={{ marginBottom: 8 }} />
            <List
                loading={candidates === null}
                dataSource={shown}
                locale={{ emptyText: candidates === null ? ' ' : 'Nothing else you could install it in.' }}
                renderItem={e => (
                    <List.Item actions={[
                        <Button key="add" type="link" loading={busy === e.id} disabled={!!busy && busy !== e.id} onClick={() => add(e)}>Install</Button>,
                    ]}>
                        <Space>
                            <EntityImage entity={e} size={24} />
                            <Text>{e.name}</Text>
                            <Tag style={{ fontSize: 11 }}>{e.entity_type}</Tag>
                        </Space>
                    </List.Item>
                )}
            />
        </Modal>
    );
}

export default AddHostModal;
